type LoginPayloadShape = {
  username: string;
  password: string;
};

function pemToArrayBuffer(pem: string): ArrayBuffer {
  const cleanPem = pem
    .replace(/-----BEGIN PUBLIC KEY-----/g, '')
    .replace(/-----END PUBLIC KEY-----/g, '')
    .replace(/\s+/g, '');

  const binaryString = globalThis.atob(cleanPem);
  const bytes = new Uint8Array(binaryString.length);
  for (let index = 0; index < binaryString.length; index += 1) {
    bytes[index] = binaryString.charCodeAt(index);
  }

  return bytes.buffer;
}

function bufferToBase64(buffer: ArrayBuffer): string {
  const bytes = new Uint8Array(buffer);
  let binary = '';
  bytes.forEach((byte) => {
    binary += String.fromCharCode(byte);
  });

  return globalThis.btoa(binary);
}

async function importPublicKey(publicKeyPem: string): Promise<CryptoKey> {
  const cryptoSubtle = globalThis.crypto?.subtle;
  if (!cryptoSubtle) {
    throw new Error('Browser crypto is unavailable');
  }

  return cryptoSubtle.importKey(
    'spki',
    pemToArrayBuffer(publicKeyPem),
    {
      name: 'RSA-OAEP',
      hash: 'SHA-256',
    },
    false,
    ['encrypt'],
  );
}

async function encryptValue(value: string, publicKeyPem: string): Promise<string> {
  const cryptoSubtle = globalThis.crypto?.subtle;
  if (!cryptoSubtle) {
    throw new Error('Browser crypto is unavailable');
  }

  const publicKey = await importPublicKey(publicKeyPem);
  const encoded = new TextEncoder().encode(value);
  const encrypted = await cryptoSubtle.encrypt({ name: 'RSA-OAEP' }, publicKey, encoded);
  return bufferToBase64(encrypted);
}

export async function encryptLoginPayload<T extends LoginPayloadShape>(
  payload: T,
  publicKeyPem: string,
): Promise<T> {
  if (!publicKeyPem?.trim()) {
    throw new Error('Login public key is missing');
  }

  return {
    ...payload,
    username: await encryptValue(payload.username, publicKeyPem),
    password: await encryptValue(payload.password, publicKeyPem),
  };
}
